import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import Scroll from '../../components/shared/Scroll';
import { ImageView } from '@play-co/timestep-core/ui';
import TabbedView from '../../components/shared/tabs/TabbedView';
import { LangBitmapOpts } from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import { DuoFriendTile } from './DuoFriendTile';
import { Feature } from 'src/replicant/analytics';
import { DuoFriend } from 'src/replicant/duo/duo.asyncGetters';
import { duoPopupMutex } from '../duo.sequence';

import { AssetGroup } from '@play-co/timestep-core';

type ListFriend = DuoFriend & {
  type: 'sent' | 'received';
};

type CloseHandler = (closeMenu?: boolean) => void;

namespace style {
  export const popup = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 1015,
    darkerBg: false,
    titleIcon: { image: 'assets/ui/care/care_invitation_icon.png' },
  };

  export const banner: ViewOpts = {
    backgroundColor: '#FFF3D9',
    x: 7,
    height: 103,
    y: 90,
  };
  banner.width = popup.width - 2 * banner.x!;

  export const bannerBorder: ViewOpts = {
    backgroundColor: '#fdc381',
    width: banner.width,
    height: 3,
  };

  export const bannerIcon: ImageViewOpts = {
    image: 'assets/ui/duo/icons/duo_display_banner.png',
    x: 10,
    y: 22,
    width: 75,
    height: 60,
  };

  export const message: LangBitmapOpts = {
    size: 20,
    width: 450,
    height: banner.height,
    x: 100,
    y: banner.y,
    zIndex: 1,
    align: 'left',
    verticalAlign: 'center',
  };

  export const tab: ViewOpts = {
    backgroundColor: '#fdc381',
  };

  export const cancelButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.cancel,
    labelOffsetY: -1,
    fontSize: 46,
    font: 'Body',
    x: popup.width / 2,
    y: popup.height - 25 - 100 / 2,
    width: 259,
    height: 100,
    centerOnOrigin: true,
  };
}

export default class PopupDuoInvitations extends PopupBasic {
  private tabs: TabbedView;
  private sentList: Scroll<ListFriend>;
  private receivedList: Scroll<ListFriend>;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...style.popup,
      superview: opts.superview,
      close: opts.close,
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.POPUP_DUO_INVITATIONS._,
      },
    });

    this.buttonClose.removeFromSuperview();

    const banner = this.box.addSubview(new View(style.banner));
    banner.addSubview(
      new View({
        ...style.bannerBorder,
        y: -style.bannerBorder.height!,
      }),
    );
    banner.addSubview(
      new View({
        ...style.bannerBorder,
        y: style.message.height,
      }),
    );
    banner.addSubview(new ImageView(style.bannerIcon));
    this.message.updateOpts(style.message);

    this.tabs = new TabbedView({
      tabs: [
        {
          label: () => i18n('duo.popup.tab-sent'),
          tracking: {
            feature: Feature.DUO._,
            $subFeature: Feature.DUO.POPUP_DUO_INVITATIONS.TAB_SENT,
          },
        },
        {
          label: () => i18n('duo.popup.tab-received'),
          tracking: {
            feature: Feature.DUO._,
            $subFeature: Feature.DUO.POPUP_DUO_INVITATIONS.TAB_RECEIVED,
          },
        },
      ],
      alignment: 'left',
      superview: this.box,
      rect: {
        x: 30,
        y: 270,
        width: this.box.style.width - 60,
        height: 600,
      },
    });

    const views = this.tabs.getViews();
    views.forEach((view) => view.updateOpts(style.tab));
    const [friendsTab, visitorsTab] = views;

    this.sentList = new Scroll<ListFriend>({
      superview: friendsTab,
      createItem: this.createItem.bind(this),
    });

    this.receivedList = new Scroll<ListFriend>({
      superview: visitorsTab,
      createItem: this.createItem.bind(this),
    });

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.cancelButton,
        localeText: () => i18n('basic.close'),
        onClick: async () => {
          try {
            sounds.sfx.play('cancel');
            return opts.close();
          } finally {
            duoPopupMutex.release();
          }
        },
        tracking: {
          feature: Feature.DUO._,
          $subFeature: Feature.DUO.POPUP_DUO_INVITATIONS.CLOSE,
        },
      }),
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/care/care_invitation_icon.png',
      'assets/ui/duo/icons/duo_display_banner.png',
    ]);
  }

  public init(opts: {
    sent: DuoFriend[];
    received: DuoFriend[];
    selectedTab?: 'received' | 'sent';
  }) {
    super.init({
      title: i18n('duo.popup.title'),
      message: i18n('duo.popup.message'),
    });

    this.sentList.setItems(
      opts.sent.map((friend) => ({ ...friend, type: 'sent' })),
    );
    this.receivedList.setItems(
      opts.received.map((friend) => ({ ...friend, type: 'received' })),
    );

    if (opts.selectedTab === 'received') {
      this.tabs.setProps({ selectedTabIndex: 1 });
      this.tabs.setProps({});
    }
  }

  private createItem(superview: View, _: number, friend: ListFriend) {
    return superview.addSubview(
      new DuoFriendTile({ friend, close: this.opts.close }).view,
    );
  }
}
